import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PlusCircle, Pencil, Trash2, Search, FlaskConical, FileCheck2, FileX2, ImagePlus, X, Barcode, Printer, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { DeviceBarcodeLabel } from "./DeviceBarcodeLabel";

export const HAZMAT_CLASS_LABELS = {
  disinfectant: "مطهّر / Disinfectant",
  acid: "حمض / Acid",
  alkali: "قلوي / Alkali",
  flammable: "قابل للاشتعال / Flammable",
  oxidizer: "مؤكسد / Oxidizer",
  toxic: "سام / Toxic",
  corrosive: "أكّال / Corrosive",
  compressed_gas: "غاز مضغوط / Compressed Gas",
  other: "أخرى / Other",
};

const CLASS_COLORS = {
  disinfectant: "bg-emerald-100 text-emerald-800",
  acid: "bg-red-100 text-red-800",
  alkali: "bg-purple-100 text-purple-800",
  flammable: "bg-orange-100 text-orange-800",
  oxidizer: "bg-amber-100 text-amber-800",
  toxic: "bg-rose-100 text-rose-800",
  corrosive: "bg-red-100 text-red-800",
  compressed_gas: "bg-cyan-100 text-cyan-800",
  other: "bg-slate-100 text-slate-700",
};

const emptyForm = {
  name: "", classification: "disinfectant", used_in: "",
  msds_available: false, notes: "", image: null, nfpa_image: null,
};

export const HazmatSection = ({ canManage, isAdmin, addSignal = 0 }) => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [formDialog, setFormDialog] = useState(null); // { mode, item? }
  const [form, setForm] = useState(emptyForm);
  const [nextNo, setNextNo] = useState("");
  const [preview, setPreview] = useState(null); // image lightbox
  const [barcodeItem, setBarcodeItem] = useState(null);
  const [printItem, setPrintItem] = useState(null);
  const [msdsFile, setMsdsFile] = useState(null);
  const [existingMsds, setExistingMsds] = useState(null);
  const [msdsBusy, setMsdsBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/hazmat`, { headers });
      setItems(res.data || []);
    } catch {
      toast.error("فشل تحميل المواد الكيميائية");
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (addSignal > 0) openCreate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSignal]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter(it => {
      if (!s) return true;
      return [it.name, it.item_no, it.used_in, it.location_name, it.notes].some(v => (v || "").toLowerCase().includes(s));
    });
  }, [items, search]);

  const onImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("حجم الصورة يجب أن يكون أقل من 3MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const onNfpaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("حجم الصورة يجب أن يكون أقل من 3MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, nfpa_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const onMsdsSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("يجب أن يكون الملف بصيغة PDF"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("حجم الملف يجب أن يكون أقل من 10MB"); return; }
    setMsdsFile(file);
    setForm(f => ({ ...f, msds_available: true }));
  };

  const openMsds = async (it) => {
    try {
      const res = await axios.get(`${API}/hazmat/${it.id}/msds`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("فشل فتح ملف MSDS");
    }
  };

  const removeExistingMsds = async (itemId) => {
    if (!window.confirm("هل تريد حذف ملف MSDS الحالي؟")) return;
    try {
      await axios.delete(`${API}/hazmat/${itemId}/msds`, { headers });
      setExistingMsds(null);
      toast.success("تم حذف ملف MSDS");
      load();
    } catch {
      toast.error("فشل حذف الملف");
    }
  };

  const openCreate = async () => {
    setForm(emptyForm);
    setNextNo("");
    setMsdsFile(null);
    setExistingMsds(null);
    setFormDialog({ mode: "create" });
    try {
      const res = await axios.get(`${API}/hazmat/next-number`, { headers });
      setNextNo(res.data.number);
    } catch { /* generated on save */ }
  };
  const openEdit = (it) => {
    setNextNo(it.item_no || "");
    setMsdsFile(null);
    setExistingMsds(it.msds_file_path ? { id: it.id, filename: it.msds_filename } : null);
    setForm({
      name: it.name, classification: it.classification,
      used_in: it.used_in || it.location_name || "",
      msds_available: !!it.msds_available,
      notes: it.notes || "", image: it.image || null, nfpa_image: it.nfpa_image || null,
    });
    setFormDialog({ mode: "edit", item: it });
  };

  const save = async () => {
    if (!form.name) { toast.error("اسم المادة مطلوب"); return; }
    const payload = { ...form, image: form.image || "", nfpa_image: form.nfpa_image || "" };
    setMsdsBusy(true);
    try {
      let savedId;
      if (formDialog.mode === "create") {
        const res = await axios.post(`${API}/hazmat`, payload, { headers });
        savedId = res.data.id;
        toast.success("تمت إضافة المادة");
      } else {
        savedId = formDialog.item.id;
        await axios.put(`${API}/hazmat/${savedId}`, payload, { headers });
        toast.success("تم تحديث المادة");
      }
      if (msdsFile && savedId) {
        const fd = new FormData();
        fd.append("file", msdsFile);
        try {
          await axios.post(`${API}/hazmat/${savedId}/msds`, fd, { headers });
          toast.success("تم رفع ملف MSDS");
        } catch (e) {
          toast.error(e.response?.data?.detail || "فشل رفع ملف MSDS");
        }
      }
      setFormDialog(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    } finally {
      setMsdsBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`هل أنت متأكد من حذف المادة "${it.name}"؟`)) return;
    try {
      await axios.delete(`${API}/hazmat/${it.id}`, { headers });
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحذف");
    }
  };

  const isExpired = (d) => d && d < new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="hazmat-section">
      {/* Filters — identical to device tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white border rounded-md px-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث باسم المادة / الرقم / الاستخدام"
            className="border-0 focus-visible:ring-0 text-right" data-testid="hazmat-search" />
        </div>
      </div>

      <div className="border rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">الصورة</TableHead>
              <TableHead className="text-right">الرقم</TableHead>
              <TableHead className="text-right">اسم المادة</TableHead>
              <TableHead className="text-right">التصنيف</TableHead>
              <TableHead className="text-right">تستخدم في</TableHead>
              <TableHead className="text-right">MSDS</TableHead>
              <TableHead className="text-right">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">لا توجد مواد كيميائية مسجّلة</TableCell></TableRow>
            )}
            {filtered.map(it => (
              <TableRow key={it.id} data-testid={`hazmat-row-${it.id}`}>
                <TableCell>
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-10 h-10 rounded-md object-cover border cursor-pointer" onClick={() => setPreview(it.image)} />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center"><FlaskConical className="w-4 h-4 text-slate-400" /></div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs text-[#655dc6] font-bold whitespace-nowrap">{it.item_no || "—"}</TableCell>
                <TableCell className="font-medium">{it.name}</TableCell>
                <TableCell>
                  <Badge className={`${CLASS_COLORS[it.classification] || CLASS_COLORS.other} border-0 text-[11px]`}>
                    {(HAZMAT_CLASS_LABELS[it.classification] || it.classification).split(" / ")[0]}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-600 max-w-[240px] truncate">{it.used_in || it.location_name || "—"}</TableCell>
                <TableCell>
                  {it.msds_file_path ? (
                    <button type="button" onClick={() => openMsds(it)} data-testid={`hz-msds-open-${it.id}`}
                      className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors">
                      <FileText className="w-3 h-3" /> متوفرة — عرض PDF
                    </button>
                  ) : it.msds_available
                    ? <Badge className="bg-emerald-50 text-emerald-700 border-0 gap-1"><FileCheck2 className="w-3 h-3" /> متوفرة</Badge>
                    : <Badge className="bg-red-50 text-red-700 border-0 gap-1"><FileX2 className="w-3 h-3" /> غير متوفرة</Badge>}
                </TableCell>
                <TableCell className="space-x-1 space-x-reverse whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setPrintItem(it)} title="طباعة تفاصيل المادة" data-testid={`hz-print-${it.id}`}>
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setBarcodeItem({ biomed_no: it.item_no || "", device_name: it.name })} title="ملصق الباركود" data-testid={`hz-barcode-${it.id}`} disabled={!it.item_no}>
                    <Barcode className="w-4 h-4" />
                  </Button>
                  {canManage && (
                    <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-hazmat-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => remove(it)} className="text-red-600 hover:bg-red-50" data-testid={`delete-hazmat-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!formDialog} onOpenChange={(o) => !o && setFormDialog(null)}>
        <DialogContent dir="rtl" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{formDialog?.mode === "create" ? "إضافة مادة كيميائية" : "تعديل المادة"}</DialogTitle>
            <DialogDescription>سجل المواد الخطرة (Hazmat)</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>رقم المادة *</Label>
              <Input value={nextNo} readOnly className="bg-slate-50 font-mono text-[#655dc6] font-bold text-right" data-testid="hz-item-no" />
              <p className="text-[10px] text-slate-400">يتولد إلكترونياً — لا يمكن تكراره</p>
            </div>
            <div className="space-y-1"><Label>اسم المادة *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-right" data-testid="hz-name" /></div>
            <div className="space-y-1 col-span-2"><Label>التصنيف *</Label>
              <Select value={form.classification} onValueChange={(v) => setForm({ ...form, classification: v })}>
                <SelectTrigger data-testid="hz-class"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(HAZMAT_CLASS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1 col-span-2"><Label>تستخدم في</Label>
              <Input value={form.used_in} onChange={(e) => setForm({ ...form, used_in: e.target.value })} placeholder="مثال: تطهير الأسطح والأدوات" className="text-right" data-testid="hz-used-in" /></div>
            <div className="space-y-1 col-span-2"><Label>نشرة السلامة MSDS</Label>
              <Select value={form.msds_available ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, msds_available: v === "yes" })}>
                <SelectTrigger data-testid="hz-msds"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">متوفرة</SelectItem>
                  <SelectItem value="no">غير متوفرة</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1 col-span-2">
              <Label>ملف MSDS (PDF)</Label>
              {existingMsds && !msdsFile && (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 text-emerald-700 truncate"><FileText className="w-4 h-4 shrink-0" /> {existingMsds.filename || "ملف MSDS مرفوع"}</span>
                  <button type="button" onClick={() => removeExistingMsds(existingMsds.id)} className="text-red-500 hover:text-red-700" data-testid="hz-msds-remove-existing">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              {msdsFile ? (
                <div className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 text-slate-700 truncate"><FileText className="w-4 h-4 shrink-0 text-[#655dc6]" /> {msdsFile.name}</span>
                  <button type="button" onClick={() => setMsdsFile(null)} className="text-red-500 hover:text-red-700" data-testid="hz-msds-clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-md p-3 cursor-pointer hover:border-[#655dc6] text-sm text-slate-500">
                  <Upload className="w-4 h-4" /> {existingMsds ? "استبدال الملف (PDF حتى 10MB)" : "اختر ملف PDF (حتى 10MB)"}
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onMsdsSelect} data-testid="hz-msds-input" />
                </label>
              )}
            </div>
            <div className="space-y-1 col-span-2">
              <Label>صورة المادة</Label>
              {form.image ? (
                <div className="relative inline-block">
                  <img src={form.image} alt="preview" className="w-24 h-24 rounded-md object-cover border" />
                  <button type="button" onClick={() => setForm({ ...form, image: null })}
                    className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full p-0.5" data-testid="hz-remove-image">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-md p-3 cursor-pointer hover:border-[#655dc6] text-sm text-slate-500">
                  <ImagePlus className="w-4 h-4" /> اختر صورة (حتى 3MB)
                  <input type="file" accept="image/*" className="hidden" onChange={onImageSelect} data-testid="hz-image-input" />
                </label>
              )}
            </div>
            <div className="space-y-1 col-span-2">
              <Label>ملصق NFPA</Label>
              {form.nfpa_image ? (
                <div className="relative inline-block">
                  <img src={form.nfpa_image} alt="nfpa" className="w-24 h-24 rounded-md object-contain border bg-white" />
                  <button type="button" onClick={() => setForm({ ...form, nfpa_image: null })}
                    className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full p-0.5" data-testid="hz-remove-nfpa">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center gap-2 border-2 border-dashed border-slate-200 rounded-md p-3 cursor-pointer hover:border-[#655dc6] text-sm text-slate-500">
                  <ImagePlus className="w-4 h-4" /> اختر صورة ملصق NFPA (حتى 3MB)
                  <input type="file" accept="image/*" className="hidden" onChange={onNfpaSelect} data-testid="hz-nfpa-input" />
                </label>
              )}
            </div>
            <div className="space-y-1 col-span-2"><Label>ملاحظات</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-right min-h-[60px]" data-testid="hz-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormDialog(null)}>إلغاء</Button>
            <Button onClick={save} disabled={msdsBusy} className="bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="save-hazmat-btn">{msdsBusy ? "جارٍ الحفظ..." : "حفظ"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Barcode label */}
      <DeviceBarcodeLabel device={barcodeItem} onClose={() => setBarcodeItem(null)} />

      {/* Material details print */}
      <Dialog open={!!printItem} onOpenChange={(o) => !o && setPrintItem(null)}>
        <DialogContent dir="rtl" className="max-w-lg hz-print-dialog">
          <DialogHeader>
            <DialogTitle>طباعة تفاصيل المادة</DialogTitle>
            <DialogDescription>ورقة A4 ببيانات المادة الكاملة</DialogDescription>
          </DialogHeader>
          <style>{`
            .hz-print-sheet { background: #fff; border: 1px solid #E5E7EB; border-radius: 8px; padding: 20px; direction: rtl; }
            .hz-print-sheet .hp-head { display: flex; flex-direction: column; align-items: center; gap: 3px; border-bottom: 2px solid #0A0A0A; padding-bottom: 10px; margin-bottom: 14px; }
            .hz-print-sheet .hp-head img { height: 44px; }
            .hz-print-sheet .hp-head .co { font-weight: 700; font-size: 13px; color: #0A0A0A; }
            .hz-print-sheet .hp-title { text-align: center; font-weight: 700; font-size: 15px; margin-bottom: 12px; color: #0A0A0A; }
            .hz-print-sheet table { width: 100%; border-collapse: collapse; font-size: 13px; }
            .hz-print-sheet td { border: 1px solid #D1D5DB; padding: 7px 10px; }
            .hz-print-sheet td.k { background: #F3F4F6; font-weight: 700; width: 32%; color: #0A0A0A; }
            .hz-print-sheet .hp-img { margin-top: 12px; display: flex; justify-content: center; gap: 20px; }
            .hz-print-sheet .hp-img-box { text-align: center; }
            .hz-print-sheet .hp-img img { max-height: 150px; border-radius: 6px; border: 1px solid #E5E7EB; }
            .hz-print-sheet .hp-img-cap { font-size: 11px; color: #6B7280; margin-top: 4px; font-weight: 600; }
            .hz-print-sheet .hp-foot { margin-top: 14px; font-size: 10px; color: #6B7280; display: flex; justify-content: space-between; }
            @media print {
              @page { size: A4 portrait; margin: 12mm; }
              #root { display: none !important; }
              html, body { margin: 0 !important; padding: 0 !important; }
              body * { visibility: hidden !important; }
              .hz-print-sheet, .hz-print-sheet * { visibility: visible !important; }
              .hz-print-dialog {
                position: static !important;
                transform: none !important;
                width: 100% !important;
                max-width: 100% !important;
                max-height: none !important;
                overflow: visible !important;
                margin: 0 !important;
                padding: 0 !important;
                border: none !important;
                box-shadow: none !important;
                border-radius: 0 !important;
              }
              .hz-print-dialog > *:not(.hz-print-sheet):not(style) { display: none !important; }
              .hz-print-sheet { border: none !important; border-radius: 0 !important; padding: 0 !important; }
              .hz-print-sheet .hp-head img { height: 60px; }
              .hz-print-sheet .hp-head .co { font-size: 15px; }
              .hz-print-sheet .hp-title { font-size: 18px; margin: 14px 0 16px; }
              .hz-print-sheet table { font-size: 14.5px; }
              .hz-print-sheet td { padding: 10px 12px; }
              .hz-print-sheet .hp-img img { max-height: 220px; }
              .hz-print-sheet .hp-img-cap { font-size: 12px; }
              .hz-print-sheet .hp-foot { margin-top: 20px; font-size: 11px; }
            }
          `}</style>
          {printItem && (
            <div className="hz-print-sheet" data-testid="hz-print-sheet">
              <div className="hp-head">
                <img src="/lucent-logo.png" alt="Lucent" />
                <div className="co">Lucent Dental Center Co.,</div>
              </div>
              <div className="hp-title">بيانات مادة كيميائية — Hazmat Material Details</div>
              <table>
                <tbody>
                  <tr><td className="k">رقم المادة</td><td style={{ fontFamily: "monospace", fontWeight: 700 }}>{printItem.item_no || "—"}</td></tr>
                  <tr><td className="k">اسم المادة</td><td>{printItem.name}</td></tr>
                  <tr><td className="k">التصنيف</td><td>{HAZMAT_CLASS_LABELS[printItem.classification] || printItem.classification}</td></tr>
                  <tr><td className="k">تستخدم في</td><td>{printItem.used_in || printItem.location_name || "—"}</td></tr>
                  <tr><td className="k">نشرة السلامة MSDS</td><td>{printItem.msds_available ? "متوفرة ✓" : "غير متوفرة ✗"}</td></tr>
                  <tr><td className="k">ملاحظات</td><td>{printItem.notes || "—"}</td></tr>
                  <tr><td className="k">أضيفت بواسطة</td><td>{printItem.created_by_name || "—"}</td></tr>
                </tbody>
              </table>
              {(printItem.image || printItem.nfpa_image) && (
                <div className="hp-img">
                  {printItem.image && (
                    <div className="hp-img-box">
                      <img src={printItem.image} alt={printItem.name} />
                      <div className="hp-img-cap">صورة المادة</div>
                    </div>
                  )}
                  {printItem.nfpa_image && (
                    <div className="hp-img-box">
                      <img src={printItem.nfpa_image} alt="NFPA" />
                      <div className="hp-img-cap">ملصق NFPA</div>
                    </div>
                  )}
                </div>
              )}
              <div className="hp-foot">
                <span>© شركة مركز لوسنت للأسنان — سجل المواد الكيميائية</span>
                <span>{new Date().toLocaleDateString("ar-SA")}</span>
              </div>
            </div>
          )}
          <Button onClick={() => window.print()} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white gap-2" data-testid="hz-print-confirm-btn">
            <Printer className="w-4 h-4" /> طباعة
          </Button>
        </DialogContent>
      </Dialog>

      {/* Image lightbox */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-xl p-2">
          <DialogHeader><DialogTitle className="sr-only">صورة المادة</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="hazmat" className="w-full rounded-md" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
